import { Dialog } from "primereact/dialog";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

type Position = "top" | "right" | "bottom" | "left";

interface InfoDotProps {
  children: ReactNode;
  position?: Position;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  text: string;
  content?: ReactNode;
}

const CloseText = styled.span`
  color: #fff;
  cursor: pointer;
  font-weight: 500;
  position: absolute;
  right: 20px;
  top: -13px;
  background: var(--sumi-blue);
  font-size: 15px;
  padding: 2px 5px;
  border-radius: 6px;
`;

const Wrapper = styled.div<{
  $position: Position;
  $marginTop?: string;
  $marginBottom?: string;
  $marginLeft?: string;
  $marginRight?: string;
}>`
  display: inline-flex;
  position: relative;
  align-items: center;

  ${({ $position, $marginTop, $marginBottom, $marginLeft, $marginRight }) =>
    $position === "top" &&
    css`
      flex-direction: column-reverse;
      .info-dot {
        margin-bottom: ${$marginBottom || "4px"};
        margin-left: ${$marginLeft || "0"};
        margin-right: ${$marginRight || "0"};
        margin-top: ${$marginTop || "0"};
      }
    `}
  ${({ $position, $marginTop, $marginBottom, $marginLeft, $marginRight }) =>
    $position === "bottom" &&
    css`
      flex-direction: column;
      .info-dot {
        margin-top: ${$marginTop || "4px"};
        margin-left: ${$marginLeft || "0"};
        margin-right: ${$marginRight || "0"};
        margin-bottom: ${$marginBottom || "0"};
      }
    `}
    ${({ $position, $marginTop, $marginBottom, $marginLeft, $marginRight }) =>
    $position === "left" &&
    css`
      flex-direction: row-reverse;
      .info-dot {
        margin-right: ${$marginRight || "4px"};
        margin-top: ${$marginTop || "0"};
        margin-bottom: ${$marginBottom || "0"};
        margin-left: ${$marginLeft || "0"};
      }
    `}
    ${({ $position, $marginTop, $marginBottom, $marginLeft, $marginRight }) =>
    $position === "right" &&
    css`
      flex-direction: row;
      .info-dot {
        margin-left: ${$marginLeft || "4px"};
        margin-top: ${$marginTop || "0"};
        margin-bottom: ${$marginBottom || "0"};
        margin-right: ${$marginRight || "0"};
      }
    `}
`;

const InfoDotIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: var(--sumi-background-blue);
  color: var(--sumi-text-primary);
  font-size: 10px;
  font-weight: bold;
  cursor: default;
  user-select: none;
  font-family: monospace;

  &:hover {
    background: var(--sumi-darker-blue);
    cursor: pointer;
  }
`;

export const InfoDot: React.FC<InfoDotProps> = ({
  children,
  position = "right",
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  text,
  content,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const handleOpen = () => setVisible(true);
  const handleClose = () => setVisible(false);

  const dialogHeader = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
      }}
    >
      <CloseText
        onClick={handleClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClose();
        }}
      >
        {t("cmd-close")}
      </CloseText>
    </div>
  );

  return (
    <Wrapper
      $position={position}
      $marginTop={marginTop}
      $marginBottom={marginBottom}
      $marginLeft={marginLeft}
      $marginRight={marginRight}
    >
      {children}
      <InfoDotIcon onClick={handleOpen} className="info-dot">
        i
      </InfoDotIcon>
      <Dialog
        header={dialogHeader}
        visible={visible}
        onHide={handleClose}
        closable={false}
        style={{ maxWidth: "400px" }}
        closeOnEscape
      >
        <div style={{ padding: "1rem" }}>{content ? content : text}</div>
      </Dialog>
    </Wrapper>
  );
};
